/**
 * ConfigRegistry - Client-side configuration storage
 *
 * Stores game configurations received from the server to eliminate
 * hardcoded config duplicates in client code.
 *
 * The server sends configs on connection via the 'gameConfigs' event.
 * This provides a single source of truth from config/*.json files.
 */

import { ItemRarity } from '../../shared/Item.js';
import { ProjectileType, Weapon, WeaponFireMode } from '../../shared/Weapon.js';
import { weaponRegistry } from '../../shared/WeaponRegistry.js';
import { ClientLogger } from '../logger/ClientLogger.js';

const configLogger = ClientLogger.getLogger('config');

class ConfigRegistry {
  constructor() {
    this.abilities = new Map();
    this.pickups = new Map();
    this.settings = {};
    this.loaded = false;
  }

  /**
   * Load configs from server data
   * Called when 'gameConfigs' event is received
   * @param {Object} data - Config data from server
   */
  load(data) {
    // Load abilities
    if (data.abilities) {
      this.abilities.clear();
      data.abilities.forEach(ability => {
        this.abilities.set(ability.id, ability);
      });
      configLogger.info('[ConfigRegistry] Abilities loaded', { count: this.abilities.size });
    }

    // Load pickups
    if (data.pickups) {
      this.pickups.clear();
      data.pickups.forEach(pickup => {
        this.pickups.set(pickup.id, pickup);
      });
      configLogger.info('[ConfigRegistry] Pickups loaded', { count: this.pickups.size });
    }

    // Load settings
    if (data.settings) {
      this.settings = data.settings;
      configLogger.info('[ConfigRegistry] Game settings loaded');
    }

    this.loaded = true;
    configLogger.info('[ConfigRegistry] All configs loaded from server');
  }

  /**
   * Get an ability config by ID
   * @param {string} abilityId
   * @returns {Object|null}
   */
  getAbility(abilityId) {
    return this.abilities.get(abilityId) || null;
  }

  /**
   * Get a pickup config by ID
   * @param {string} pickupId
   * @returns {Object|null}
   */
  getPickup(pickupId) {
    return this.pickups.get(pickupId) || null;
  }

  /**
   * Get a pickup config by ability ID
   * @param {string} abilityId
   * @returns {Object|null}
   */
  getPickupByAbilityId(abilityId) {
    for (const pickup of this.pickups.values()) {
      if (pickup.abilityId === abilityId) {
        return pickup;
      }
    }
    return null;
  }

  /**
   * Get graphic path for an ability pickup
   * @param {string} abilityId
   * @returns {string|null}
   */
  getAbilityPickupGraphic(abilityId) {
    const pickup = this.getPickupByAbilityId(abilityId);
    return pickup?.graphic || null;
  }

  /**
   * Get icon path for an ability
   * @param {string} abilityId
   * @returns {string|null}
   */
  getAbilityIcon(abilityId) {
    const ability = this.getAbility(abilityId);
    return ability?.icon || null;
  }

  /**
   * Populate the shared weapon registry from the server's weapons.json.
   * (Moved here from shared/WeaponRegistry.js in Phase 3C: shared/ no longer
   * knows how either environment loads its data.)
   * @returns {Promise<boolean>} true when loaded from the server, false on fallback
   */
  async loadWeapons() {
    try {
      const response = await fetch('/config/weapons.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch weapons.json: ${response.status}`);
      }
      const configData = await response.json();
      const weaponData = Array.isArray(configData) ? configData : configData.weapons;
      weaponRegistry.registerBulk(weaponData);
      configLogger.info('[ConfigRegistry] Weapons loaded', { count: weaponRegistry.getAll().length });
      return true;
    } catch (error) {
      configLogger.error('[ConfigRegistry] Failed to load weapons from server:', error);
      // Fallback: register a minimal default weapon so the client can still render
      weaponRegistry.register(new Weapon({
        id: 'assault_rifle',
        name: 'Assault Rifle',
        description: 'Standard rifle',
        rarity: ItemRarity.COMMON,
        fireMode: WeaponFireMode.SEMI_AUTO,
        fireRate: 200,
        damage: 10,
        magCapacity: 30,
        ammoType: 'rifle',
        reloadTime: 1500,
        projectileType: ProjectileType.BULLET,
        projectileSpeed: 1600,
        projectileLifetime: 3000,
        projectileSize: 4,
        tracerColor: '#ffff00'
      }));
      configLogger.warn('[ConfigRegistry] Using minimal fallback weapon');
      return false;
    }
  }

  /**
   * Check if configs have been loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Get all abilities
   * @returns {Array}
   */
  getAllAbilities() {
    return Array.from(this.abilities.values());
  }

  /**
   * Get all pickups
   * @returns {Array}
   */
  getAllPickups() {
    return Array.from(this.pickups.values());
  }
}

// Create singleton instance
const configRegistry = new ConfigRegistry();

// Export globally

export { configLogger, ConfigRegistry, configRegistry };
